import storefront from "../global/storefront";
import {
  PRODUCT_CARD_GROUPING_NODES_QUERY,
  PRODUCT_COLLECTION_MEMBERSHIP_QUERY,
  PRODUCT_SWATCH_COLOR_PATTERNS_QUERY,
} from "../global/storefront/queries";

const maxVisibleSwatches = 4;

const maxIdsPerNodesRequest = 250;

export const pendingRevealAttr = "data-pending-reveal";

const groupTreeCache = new Map();
const hydratedSwatchCache = new Map();
const productCollectionMembershipCache = new Map();

const batchFailed = Symbol("batch-failed");

export function chunk(array, size) {
  const chunks = [];

  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  return chunks;
}

function referencedIds(field) {
  return ((field && field.references && Array.isArray(field.references.nodes) && field.references.nodes) || [])
    .map((node) => node && node.id)
    .filter(Boolean);
}

export function isSwatchProductFromTags(tags) {
  return (
    Array.isArray(tags) &&
    (tags.includes("product_type:Swatch") || tags.includes("Mega Swatches") || tags.includes("Multi Swatch"))
  );
}

function getStyleGroupTag(tags) {
  const styleGroupTag = Array.isArray(tags) ? tags.find((tag) => tag.includes("style_group:")) : null;

  return styleGroupTag ? styleGroupTag.replace("style_group:", "").trim() : "";
}

function resolveDisplayPrice(price, isSwatchProduct) {
  if (isSwatchProduct && !window.customerHasFreeSwatch) {
    return { price: 0, compareAtPrice: price };
  }

  return { price, compareAtPrice: 0 };
}

function numericIdFromGid(gid) {
  const match = typeof gid === "string" && gid.match(/(\d+)$/);

  return match ? match[1] : null;
}

function extractColorFamilyIds(colorFamilyListField) {
  return (colorFamilyListField?.references?.nodes || []).map((node) => node?.id).filter(Boolean);
}

function extractHydratedSwatchData(node) {
  if (!node) {
    return null;
  }

  const colorPatternNode = node.colorPattern?.references?.nodes?.[0];
  const images = node.images?.nodes || [];

  return {
    productId: node.id,
    title: node.title || "",
    url: node.handle ? `/products/${node.handle}` : "#",
    available: node.availableForSale !== false,
    imageUrl: images[0]?.url || node.featuredImage?.url || null,
    imageAlt: images[0]?.altText || node.featuredImage?.altText || node.title || "",
    hoverImageUrl: images[1]?.url || null,
    price: node.priceRange?.minVariantPrice?.amount ? Number(node.priceRange.minVariantPrice.amount) : null,
    currencyCode: node.priceRange?.minVariantPrice?.currencyCode || null,
    swatchImageUrl: colorPatternNode?.swatchImage?.reference?.image?.url || null,
    swatchColor: colorPatternNode?.swatchColor?.value || null,
    swatchLabel: colorPatternNode?.swatchLabel?.value || null,

    color: colorPatternNode?.swatchLabel?.value || null,
    colorCode: colorPatternNode?.swatchLabel?.value?.split(" - ")?.pop()?.trim() || null,
    styleGroup: getStyleGroupTag(node.tags) || null,
    colorFamilyIds: extractColorFamilyIds(node.colorFamilyList),
    variantId: numericIdFromGid(node.variants?.nodes?.[0]?.id),
    isSwatchProduct: isSwatchProductFromTags(node.tags),
  };
}

function applyAddToBagTarget(
  card,
  { variantId, isSwatchProduct, color, colorCode, styleGroup, deliveryOption, forceShowOnGrouped = false } = {},
) {
  const button = card.querySelector("[data-product-card-add-to-bag]");

  if (!button) {
    return;
  }

  delete button.dataset.state;
  button.disabled = false;

  const isAmbiguouslyGrouped = Boolean(card.dataset.groupedBucketKey) && !forceShowOnGrouped;

  if (!isAmbiguouslyGrouped && isSwatchProduct && variantId) {
    button.dataset.variantId = variantId;

    if (color) {
      button.dataset.color = color;
    } else {
      delete button.dataset.color;
    }

    if (colorCode) {
      button.dataset.colorCode = colorCode;
    } else {
      delete button.dataset.colorCode;
    }

    if (styleGroup) {
      button.dataset.styleGroup = styleGroup;
    } else {
      delete button.dataset.styleGroup;
    }

    // Carried per product (captured from its server-rendered card) so the
    // quick-add line item gets the same delivery time properties as the PDP.
    if (deliveryOption) {
      button.dataset.deliveryOption = deliveryOption;
    } else {
      delete button.dataset.deliveryOption;
    }

    button.hidden = false;
  } else {
    button.hidden = true;
    delete button.dataset.variantId;
    delete button.dataset.color;
    delete button.dataset.colorCode;
    delete button.dataset.styleGroup;
    delete button.dataset.deliveryOption;
  }
}

async function hydrateSwatchProducts(productIds) {
  const idsToFetch = productIds.filter((id) => !hydratedSwatchCache.has(id));

  if (idsToFetch.length) {
    const batches = chunk(idsToFetch, maxIdsPerNodesRequest);

    const responses = await Promise.all(
      batches.map((batchIds) =>
        storefront
          .request({
            query: PRODUCT_SWATCH_COLOR_PATTERNS_QUERY,
            variables: { ids: batchIds },
          })
          .then((response) => ({ batchIds, response }))
          .catch((error) => {
            console.error("[PLP grouping] Failed hydrating swatches:", error);

            return { batchIds, response: null };
          }),
      ),
    );

    responses.forEach(({ batchIds, response }) => {
      const nodes = response?.data?.nodes;

      if (!Array.isArray(nodes)) {
        return;
      }

      batchIds.forEach((id, index) => {
        hydratedSwatchCache.set(id, extractHydratedSwatchData(nodes[index]));
      });
    });
  }

  return productIds.map((id) => hydratedSwatchCache.get(id)).filter(Boolean);
}

function extractCollectionIds(product) {
  return new Set((product?.collections?.nodes || []).map((collection) => collection?.id).filter(Boolean));
}

async function primeProductCollectionMembership(productIds) {
  const idsToFetch = [...new Set(productIds.filter(Boolean))].filter((id) => !productCollectionMembershipCache.has(id));

  if (!idsToFetch.length) {
    return;
  }

  const batches = chunk(idsToFetch, maxIdsPerNodesRequest);
  const responses = await Promise.all(
    batches.map((batchIds) =>
      storefront
        .request({
          query: PRODUCT_COLLECTION_MEMBERSHIP_QUERY,
          variables: { ids: batchIds },
        })
        .then((response) => ({ batchIds, response }))
        .catch((error) => {
          console.error("[PLP grouping] Failed resolving product collection membership:", error);

          return { batchIds, response: null };
        }),
    ),
  );

  responses.forEach(({ batchIds, response }) => {
    const nodes = response?.data?.nodes;

    if (!Array.isArray(nodes)) {
      return;
    }

    batchIds.forEach((id, index) => {
      productCollectionMembershipCache.set(id, extractCollectionIds(nodes[index]));
    });
  });
}

function productIsInCollection(productId, collectionId) {
  if (!collectionId) {
    return true;
  }

  return productCollectionMembershipCache.get(productId)?.has(collectionId) === true;
}

function applyColorPatternToSwatch(button, swatchData) {
  if (!button || !swatchData) {
    return;
  }

  if (swatchData.swatchImageUrl) {
    button.style.backgroundColor = "";
    button.style.backgroundImage = `url('${swatchData.swatchImageUrl}')`;
    button.style.backgroundSize = "cover";
    button.style.backgroundPosition = "center";
    return;
  }

  if (swatchData.swatchColor) {
    button.style.backgroundImage = "none";
    button.style.backgroundColor = swatchData.swatchColor;
  }
}

async function normalizeSwatchColors(representative) {
  const swatchesContainer = representative.querySelector("[data-product-card-swatches]");

  if (!swatchesContainer) {
    return;
  }

  const swatches = Array.from(swatchesContainer.querySelectorAll(".product-card__swatch")).slice(0, maxVisibleSwatches);
  const productIds = swatches.map((swatch) => swatch.dataset.productId).filter(Boolean);

  if (!productIds.length) {
    return;
  }

  const hydratedProducts = await hydrateSwatchProducts(productIds);
  const byProductId = new Map(hydratedProducts.map((data) => [data.productId, data]));

  swatches.forEach((swatch) => {
    const data = byProductId.get(swatch.dataset.productId);

    if (!data) {
      return;
    }

    applyColorPatternToSwatch(swatch, data);

    const label = data.swatchLabel || data.title;

    if (label) {
      swatch.title = label;
      swatch.setAttribute("aria-label", label);
    }
  });
}

function addHydratedSwatch(representative, data) {
  const swatchesContainer = representative.querySelector("[data-product-card-swatches]");

  if (!swatchesContainer) {
    return;
  }

  const alreadyPresent = Array.from(swatchesContainer.querySelectorAll(".product-card__swatch")).some(
    (button) => button.dataset.productId === data.productId,
  );

  if (alreadyPresent) {
    return;
  }

  const button = document.createElement("button");

  button.type = "button";
  button.className = "product-card__swatch";
  button.dataset.productId = data.productId;
  button.setAttribute("aria-current", "false");

  if (!data.available) {
    button.classList.add("is-sold-out");
  }

  applyColorPatternToSwatch(button, data);

  const label = data.swatchLabel || data.title;

  if (label) {
    button.title = label;
    button.setAttribute("aria-label", label);
  }

  button.__productCardData = {
    hydrated: true,
    productId: data.productId,
    available: data.available,
    url: data.url,
    imageUrl: data.imageUrl,
    imageAlt: data.imageAlt,
    hoverImageUrl: data.hoverImageUrl,
    price: data.price,
    currencyCode: data.currencyCode,
    variantId: data.variantId,
    isSwatchProduct: data.isSwatchProduct,
  };

  button.addEventListener("click", () => {
    selectSwatch(representative, button);
  });

  const moreEl = swatchesContainer.querySelector(".product-card__swatch-more");

  if (moreEl) {
    swatchesContainer.insertBefore(button, moreEl);
  } else {
    swatchesContainer.appendChild(button);
  }

  swatchesContainer.style.display = "";

  updateSwatchVisibility(swatchesContainer, representative);
}

function productMatchesColorFamily(data, activeFamilyId) {
  return (data?.colorFamilyIds || []).includes(activeFamilyId);
}

async function ensureMinimumSwatches(
  representative,
  bucket,
  collectionId,
  membershipReadyPromise = null,
  activeFamilyId = null,
) {
  const swatchesContainer = representative.querySelector("[data-product-card-swatches]");

  if (!swatchesContainer) {
    return;
  }

  const getExistingIds = () =>
    new Set(
      Array.from(swatchesContainer.querySelectorAll(".product-card__swatch"))
        .map((swatch) => swatch.dataset.productId)
        .filter(Boolean),
    );

  let existingIds = getExistingIds();

  if (collectionId) {
    if (membershipReadyPromise) {
      await membershipReadyPromise;
    } else {
      await primeProductCollectionMembership(bucket.productIds);
    }
  }

  const eligibleProductIds = collectionId
    ? bucket.productIds.filter((productId) => productIsInCollection(productId, collectionId))
    : bucket.productIds;

  representative.dataset.groupTotalCount = String(eligibleProductIds.length);

  if (existingIds.size >= maxVisibleSwatches) {
    updateSwatchVisibility(swatchesContainer, representative);

    return;
  }

  const missingSlots = Math.max(maxVisibleSwatches - existingIds.size, 0);
  const remainingCandidateIds = eligibleProductIds.filter((productId) => !existingIds.has(productId));

  let productsToAdd;

  if (activeFamilyId) {
    const hydratedCandidates = await hydrateSwatchProducts(remainingCandidateIds);

    productsToAdd = [...hydratedCandidates]
      .sort(
        (a, b) =>
          Number(productMatchesColorFamily(b, activeFamilyId)) - Number(productMatchesColorFamily(a, activeFamilyId)),
      )
      .slice(0, missingSlots);
  } else {
    const idsToHydrate = remainingCandidateIds.slice(0, missingSlots);

    productsToAdd = await hydrateSwatchProducts(idsToHydrate);
  }

  for (const data of productsToAdd) {
    existingIds = getExistingIds();

    if (existingIds.size >= maxVisibleSwatches) {
      break;
    }

    if (!data || !data.productId || existingIds.has(data.productId)) {
      continue;
    }

    addHydratedSwatch(representative, data);
  }

  updateSwatchVisibility(swatchesContainer, representative);
}

function collectBuckets(node, buckets) {
  if (!node) return;

  const solids = referencedIds(node.solids);
  const florals = referencedIds(node.florals);
  const homeTryOn = referencedIds(node.homeTryOn);

  if (solids.length) {
    buckets.set(`${node.id}::solids`, { type: "solids", productIds: solids, productIdSet: new Set(solids) });
  }

  if (florals.length) {
    buckets.set(`${node.id}::florals`, { type: "florals", productIds: florals, productIdSet: new Set(florals) });
  }

  if (homeTryOn.length) {
    buckets.set(`${node.id}::home-try-on`, {
      type: "home-try-on",
      productIds: homeTryOn,
      productIdSet: new Set(homeTryOn),
    });
  }

  const options =
    (node.options &&
      node.options.references &&
      Array.isArray(node.options.references.nodes) &&
      node.options.references.nodes) ||
    [];

  options.forEach((child) => {
    collectBuckets(child, buckets);
  });
}

async function fetchGroupNodesInBatches(ids) {
  if (!ids.length) {
    return [];
  }

  const batches = chunk(ids, maxIdsPerNodesRequest);
  const responses = await Promise.all(
    batches.map((batchIds) =>
      storefront.request({ query: PRODUCT_CARD_GROUPING_NODES_QUERY, variables: { ids: batchIds } }).catch((error) => {
        console.error("[PLP grouping] Product Group request failed:", error);

        return undefined;
      }),
    ),
  );

  return batches.flatMap((batchIds, batchIndex) => {
    const response = responses[batchIndex];
    const nodes = response && response.data && Array.isArray(response.data.nodes) && response.data.nodes;

    if (nodes) {
      return nodes;
    }

    return batchIds.map(() => batchFailed);
  });
}

async function resolveGroupingNodes(groupIds) {
  const uncachedGroupIds = groupIds.filter((id) => !groupTreeCache.has(id));

  if (uncachedGroupIds.length) {
    const nodes = await fetchGroupNodesInBatches(uncachedGroupIds);

    uncachedGroupIds.forEach((id, index) => {
      const node = nodes[index];

      if (node === batchFailed) {
        return;
      }

      const buckets = new Map();

      if (node) {
        collectBuckets(node, buckets);
      }

      groupTreeCache.set(id, buckets);
    });
  }

  const buckets = new Map();

  groupIds.forEach((groupId) => {
    const groupBuckets = groupTreeCache.get(groupId);

    if (!groupBuckets) return;

    groupBuckets.forEach((bucket, bucketKey) => {
      buckets.set(bucketKey, bucket);
    });
  });

  return buckets;
}

function findExistingRepresentative(grid, bucketKey) {
  return Array.from(grid.querySelectorAll("[data-grouped-bucket-key]")).find(
    (card) => card.dataset.groupedBucketKey === bucketKey,
  );
}

function captureCardData(card) {
  const titleLink = card.querySelector("[data-product-card-title]");
  const imageEl = card.querySelector("[data-product-card-image]");
  const hoverImageEl = card.querySelector("[data-product-card-image-hover]");
  const badgesEl = card.querySelector("[data-product-card-badges]");
  const priceEl = card.querySelector("price-snippet");
  const swatchEl = card.querySelector(".product-card__swatch");
  const addToBagBtn = card.querySelector("[data-product-card-add-to-bag]");
  const imageHtml = imageEl ? imageEl.innerHTML : "";

  return {
    productId: card.dataset.productId,
    available: card.dataset.available !== "false",
    url: titleLink ? titleLink.getAttribute("href") : "#",
    title: titleLink ? titleLink.textContent.trim() : "",
    imageHtml,
    hoverImageHtml: hoverImageEl ? hoverImageEl.innerHTML : "",
    badgesHtml: badgesEl ? badgesEl.innerHTML : "",
    priceOuterHtml: priceEl ? priceEl.outerHTML : "",
    swatchStyle: swatchEl ? swatchEl.getAttribute("style") : null,
    swatchLabel: swatchEl ? swatchEl.getAttribute("title") : "",

    variantId: addToBagBtn ? addToBagBtn.dataset.variantId || null : null,
    color: addToBagBtn ? addToBagBtn.dataset.color || null : null,
    colorCode: addToBagBtn ? addToBagBtn.dataset.colorCode || null : null,
    styleGroup: addToBagBtn ? addToBagBtn.dataset.styleGroup || null : null,
    deliveryOption: addToBagBtn ? addToBagBtn.dataset.deliveryOption || null : null,
    isSwatchProduct: Boolean(addToBagBtn && !addToBagBtn.hidden),
  };
}

function applyHydratedProduct(card, data, forceShowOnGrouped = false) {
  const imageContainer = card.querySelector("[data-product-card-image]");

  if (imageContainer && data.imageUrl) {
    imageContainer.querySelectorAll("source").forEach((source) => {
      source.srcset = data.imageUrl;
    });

    const image = imageContainer.querySelector("img");

    if (image) {
      image.src = data.imageUrl;
      image.srcset = data.imageUrl;

      if (data.imageAlt) {
        image.alt = data.imageAlt;
      }
    }
  }

  const hoverContainer = card.querySelector("[data-product-card-image-hover]");

  if (hoverContainer) {
    if (data.hoverImageUrl) {
      hoverContainer.querySelectorAll("source").forEach((source) => {
        source.srcset = data.hoverImageUrl;
      });

      const hoverImage = hoverContainer.querySelector("img");

      if (hoverImage) {
        hoverImage.src = data.hoverImageUrl;
        hoverImage.srcset = data.hoverImageUrl;
        hoverImage.alt = data.imageAlt || "";
      }

      hoverContainer.hidden = false;
    } else {
      hoverContainer.hidden = true;
    }
  }

  card.querySelectorAll("[data-product-card-link]").forEach((link) => {
    link.setAttribute("href", data.url);
  });

  const titleLink = card.querySelector("[data-product-card-title]");

  if (titleLink) {
    titleLink.setAttribute("href", data.url);
  }

  if (data.price !== null) {
    const priceEl = card.querySelector("price-snippet");

    if (priceEl) {
      const { price, compareAtPrice } = resolveDisplayPrice(data.price, data.isSwatchProduct);

      priceEl.setAttribute("price", String(price));
      priceEl.setAttribute("currency", data.currencyCode || "USD");
      priceEl.setAttribute("compare-at-price", String(compareAtPrice));

      priceEl.dataset.basePrice = String(price);
      priceEl.dataset.baseCompareAtPrice = String(compareAtPrice);
      priceEl.dataset.rawPrice = String(data.price);
      priceEl.dataset.swatchProduct = String(Boolean(data.isSwatchProduct));
      priceEl.dataset.freePrice = String(Boolean(data.isSwatchProduct) && !window.customerHasFreeSwatch);
    }
  }

  const wishlistBtn = card.querySelector("[data-add-to-wishlist]");

  if (wishlistBtn) {
    wishlistBtn.dataset.productId = data.productId;
    wishlistBtn.dataset.wishlistProductId = numericIdFromGid(data.productId) || "";
    wishlistBtn.dataset.wishlistVariantId = data.variantId || "";
    wishlistBtn.dataset.wishlistProductUrl = data.url ? `${window.location.origin}${data.url}` : "";
  }

  applyAddToBagTarget(card, { ...data, forceShowOnGrouped });
}

export function hydrateApiProductCard(card, node, { childMode = false } = {}) {
  if (!card || !node) {
    return;
  }

  const data = extractHydratedSwatchData(node);

  if (!data) {
    return;
  }

  data.hydrated = true;
  card.dataset.productId = node.id;
  card.dataset.available = node.availableForSale === false ? "false" : "true";

  const productGroupId = node.productGroup?.reference?.id;

  if (productGroupId) {
    card.dataset.productGroupId = productGroupId;
  } else {
    delete card.dataset.productGroupId;
  }

  delete card.dataset.groupedBucketKey;
  delete card.dataset.groupTotalCount;
  delete card.dataset.homeTryOn;

  card.removeAttribute("data-api-filter-hidden");
  card.removeAttribute(pendingRevealAttr);
  card.hidden = false;

  applyHydratedProduct(card, data);

  const titleLink = card.querySelector("[data-product-card-title]");

  if (titleLink) {
    titleLink.textContent = String(node.title || "")
      .split(" | ")[0]
      .trim();
  }

  const badges = card.querySelector("[data-product-card-badges]");

  if (badges) {
    badges.innerHTML = "";
    badges.style.display = "none";
  }

  if (childMode) {
    const swatchesContainer = card.querySelector("[data-product-card-swatches]");

    if (swatchesContainer) {
      swatchesContainer.innerHTML = "";

      const swatch = document.createElement("button");

      swatch.type = "button";
      swatch.className = "product-card__swatch is-active";
      swatch.dataset.productId = node.id;
      swatch.setAttribute("aria-current", "true");

      const colorPatternNode = node.colorPattern?.references?.nodes?.[0];
      const swatchColor = colorPatternNode?.swatchColor?.value;
      const swatchImage = colorPatternNode?.swatchImage?.reference?.image?.url;
      const swatchLabel = colorPatternNode?.swatchLabel?.value || node.title;

      if (swatchImage) {
        swatch.style.backgroundImage = `url('${swatchImage}')`;
        swatch.style.backgroundSize = "cover";
        swatch.style.backgroundPosition = "center";
      } else if (swatchColor) {
        swatch.style.backgroundColor = swatchColor;
      }

      if (swatchLabel) {
        swatch.title = swatchLabel;
        swatch.setAttribute("aria-label", swatchLabel);
      }

      swatchesContainer.appendChild(swatch);
      swatchesContainer.style.display = "";
    }
  }
}

function applyActiveProduct(card, data, forceShowOnGrouped = false) {
  card.dataset.productId = data.productId;
  card.dataset.available = data.available ? "true" : "false";

  if (data.hydrated) {
    applyHydratedProduct(card, data, forceShowOnGrouped);

    return;
  }

  const imageEl = card.querySelector("[data-product-card-image]");
  const hoverImageEl = card.querySelector("[data-product-card-image-hover]");
  const badgesEl = card.querySelector("[data-product-card-badges]");
  const priceEl = card.querySelector("price-snippet");
  const wishlistBtn = card.querySelector("[data-add-to-wishlist]");

  if (imageEl) {
    imageEl.innerHTML = data.imageHtml;
  }

  if (hoverImageEl) {
    hoverImageEl.innerHTML = data.hoverImageHtml;
  }

  if (badgesEl) {
    badgesEl.innerHTML = data.badgesHtml;
    badgesEl.style.display = data.badgesHtml ? "" : "none";
  }

  if (priceEl && data.priceOuterHtml) {
    const newPriceEl = new DOMParser().parseFromString(data.priceOuterHtml, "text/html").querySelector("price-snippet");

    if (newPriceEl) {
      priceEl.replaceWith(newPriceEl);

      // data.priceOuterHtml was captured once (see captureCardData) and can be stale relative
      // to the live cart by the time a shopper clicks this swatch — resync it against the
      // current free-swatch state rather than trusting the snapshot.
      window.syncSwatchPriceSnippet?.(newPriceEl);
    }
  }

  card.querySelectorAll("[data-product-card-link]").forEach((link) => {
    link.setAttribute("href", data.url);

    if (link.hasAttribute("data-product-card-title")) {
      link.textContent = data.title;
    }
  });

  if (wishlistBtn) {
    wishlistBtn.dataset.productId = data.productId;

    if (data.variantId) {
      wishlistBtn.dataset.wishlistProductId = numericIdFromGid(data.productId) || "";
      wishlistBtn.dataset.wishlistVariantId = data.variantId;
      wishlistBtn.dataset.wishlistProductUrl = data.url ? `${window.location.origin}${data.url}` : "";
    }
  }

  applyAddToBagTarget(card, { ...data, forceShowOnGrouped });
}

function updateSwatchVisibility(swatchesContainer, representative) {
  const buttons = Array.from(swatchesContainer.querySelectorAll(".product-card__swatch"));

  let moreEl = swatchesContainer.querySelector(".product-card__swatch-more");

  buttons.forEach((button, index) => {
    button.style.display = index < maxVisibleSwatches ? "" : "none";
  });

  const totalCount = Number.parseInt(representative.dataset.groupTotalCount, 10);
  const knownCount = Number.isNaN(totalCount) ? buttons.length : totalCount;
  const remaining = Math.max(0, knownCount - maxVisibleSwatches);

  if (remaining > 0) {
    if (!moreEl) {
      moreEl = document.createElement("a");
      moreEl.className = "product-card__swatch-more body-xsmall";

      swatchesContainer.appendChild(moreEl);
    }

    moreEl.textContent = `+${remaining}`;

    const titleLink = representative.querySelector("[data-product-card-title]");

    moreEl.setAttribute("href", titleLink ? titleLink.getAttribute("href") : "#");
  } else if (moreEl) {
    moreEl.remove();
  }
}

function selectSwatch(representative, button) {
  const data = button.__productCardData;

  if (!data) return;

  applyActiveProduct(representative, data, representative.dataset.addToBagUnlocked === "true");

  const swatchesContainer = representative.querySelector("[data-product-card-swatches]");

  if (!swatchesContainer) {
    return;
  }

  swatchesContainer.querySelectorAll(".product-card__swatch").forEach((swatch) => {
    const isActive = swatch === button;

    swatch.classList.toggle("is-active", isActive);
    swatch.setAttribute("aria-current", isActive ? "true" : "false");
  });

  updateSwatchVisibility(swatchesContainer, representative);
}

function addSwatch(representative, data, isActive) {
  const swatchesContainer = representative.querySelector("[data-product-card-swatches]");

  if (!swatchesContainer) {
    return;
  }

  const alreadyPresent = Array.from(swatchesContainer.querySelectorAll(".product-card__swatch")).some(
    (button) => button.dataset.productId === data.productId,
  );

  if (alreadyPresent) {
    return;
  }

  const button = document.createElement("button");

  button.type = "button";
  button.className = "product-card__swatch";

  if (isActive) {
    button.classList.add("is-active");
  }

  if (!data.available) {
    button.classList.add("is-sold-out");
  }

  button.setAttribute("aria-current", isActive ? "true" : "false");
  button.dataset.productId = data.productId;

  const label = data.swatchLabel || data.title;

  if (data.swatchStyle) {
    button.setAttribute("style", data.swatchStyle);
  }

  if (label) {
    button.title = label;
    button.setAttribute("aria-label", label);
  }

  button.__productCardData = data;

  button.addEventListener("click", () => {
    selectSwatch(representative, button);
  });

  const moreEl = swatchesContainer.querySelector(".product-card__swatch-more");

  if (moreEl) {
    swatchesContainer.insertBefore(button, moreEl);
  } else {
    swatchesContainer.appendChild(button);
  }

  swatchesContainer.style.display = "";

  updateSwatchVisibility(swatchesContainer, representative);
}

function makeRepresentative(card, bucketKey, totalCount, specificColorSelected) {
  const ownData = captureCardData(card);

  card.dataset.groupedBucketKey = bucketKey;
  card.dataset.groupTotalCount = String(totalCount);

  if (specificColorSelected) {
    card.dataset.addToBagUnlocked = "true";
  } else {
    delete card.dataset.addToBagUnlocked;
  }

  applyAddToBagTarget(card, specificColorSelected ? { ...ownData, forceShowOnGrouped: true } : {});

  const swatchesContainer = card.querySelector("[data-product-card-swatches]");

  if (swatchesContainer) {
    swatchesContainer.innerHTML = "";
  }

  addSwatch(card, ownData, true);
}

function getUngroupedCards(grid) {
  return Array.from(grid.querySelectorAll("[data-product-card]")).filter(
    (card) => card.dataset.productGroupId && !card.dataset.groupedBucketKey,
  );
}

function sortByBucketOrder(cards, bucket) {
  const rank = new Map(bucket.productIds.map((productId, index) => [productId, index]));

  return [...cards].sort((a, b) => {
    const rankA = rank.has(a.dataset.productId) ? rank.get(a.dataset.productId) : bucket.productIds.length;
    const rankB = rank.has(b.dataset.productId) ? rank.get(b.dataset.productId) : bucket.productIds.length;

    return rankA - rankB;
  });
}

function groupSwatchBucket(
  grid,
  bucketKey,
  bucket,
  collectionId,
  membershipReadyPromise,
  activeFamilyId,
  specificColorSelected,
) {
  const cards = getUngroupedCards(grid);
  const membersInDomOrder = cards.filter((card) => bucket.productIdSet.has(card.dataset.productId));

  if (!membersInDomOrder.length) {
    return;
  }

  const interimCount = membersInDomOrder.length;
  const existingRepresentative = findExistingRepresentative(grid, bucketKey);

  if (existingRepresentative) {
    existingRepresentative.dataset.groupTotalCount = String(interimCount);

    sortByBucketOrder(membersInDomOrder, bucket).forEach((card) => {
      addSwatch(existingRepresentative, captureCardData(card), false);
      card.remove();
    });

    ensureMinimumSwatches(existingRepresentative, bucket, collectionId, membershipReadyPromise, activeFamilyId)
      .then(() => normalizeSwatchColors(existingRepresentative))
      .catch((error) => {
        console.error("[PLP grouping] Failed hydrating/normalizing swatches:", error);
      });

    return;
  }

  const [representative, ...siblings] = membersInDomOrder;

  makeRepresentative(representative, bucketKey, interimCount, specificColorSelected);

  sortByBucketOrder(siblings, bucket).forEach((card) => {
    addSwatch(representative, captureCardData(card), false);
    card.remove();
  });

  ensureMinimumSwatches(representative, bucket, collectionId, membershipReadyPromise, activeFamilyId)
    .then(() => normalizeSwatchColors(representative))
    .catch((error) => {
      console.error("[PLP grouping] Failed hydrating/normalizing swatches:", error);
    });
}

function handleHomeTryOnBucket(bucketKey, productIds, grid) {
  const productIdSet = new Set(productIds);

  Array.from(grid.querySelectorAll("[data-product-card]"))
    .filter((card) => productIdSet.has(card.dataset.productId))
    .forEach((card) => {
      card.dataset.groupedBucketKey = `${bucketKey}::${card.dataset.productId}`;
      card.dataset.homeTryOn = "true";
    });
}

export async function runGroupingPass(
  grid,
  collectionId,
  { activeColorFamily = null, specificColorSelected = false } = {},
) {
  const activeFamilyId = activeColorFamily?.metaobjectId || null;

  const ungrouped = getUngroupedCards(grid);

  if (!ungrouped.length) {
    return;
  }

  const loadedProductIds = new Set(ungrouped.map((card) => card.dataset.productId).filter(Boolean));
  const groupIds = Array.from(new Set(ungrouped.map((card) => card.dataset.productGroupId).filter(Boolean)));

  if (!groupIds.length) {
    return;
  }

  const buckets = await resolveGroupingNodes(groupIds);

  const relevantBuckets = Array.from(buckets.entries()).filter(([, bucket]) => {
    if (bucket.type !== "solids" && bucket.type !== "florals") {
      return false;
    }

    return bucket.productIds.some((productId) => loadedProductIds.has(productId));
  });
  const relevantCandidateIds = Array.from(new Set(relevantBuckets.flatMap(([, bucket]) => bucket.productIds)));
  const membershipReadyPromise = collectionId
    ? primeProductCollectionMembership(relevantCandidateIds)
    : Promise.resolve();

  for (const [bucketKey, bucket] of buckets) {
    const { type } = bucket;

    if (type === "home-try-on") {
      const hasLoadedMember = bucket.productIds.some((productId) => loadedProductIds.has(productId));

      if (hasLoadedMember) {
        handleHomeTryOnBucket(bucketKey, bucket.productIds, grid);
      }

      continue;
    }

    if (type !== "solids" && type !== "florals") {
      continue;
    }

    const hasLoadedMember = bucket.productIds.some((productId) => loadedProductIds.has(productId));

    if (!hasLoadedMember) {
      continue;
    }

    groupSwatchBucket(
      grid,
      bucketKey,
      bucket,
      collectionId,
      membershipReadyPromise,
      activeFamilyId,
      specificColorSelected,
    );
  }
}


export function createGroupedCollectionCounter() {
  const cardKeys = new Set();

  return {
    async addProducts(products = []) {
      const normalized = products
        .map((product) => ({
          id: product?.id,
          productGroupId: product?.productGroupId || null,
        }))
        .filter((product) => product.id);

      if (!normalized.length) {
        return cardKeys.size;
      }

      const groupIds = Array.from(new Set(normalized.map((product) => product.productGroupId).filter(Boolean)));

      if (groupIds.length) {
        await resolveGroupingNodes(groupIds);
      }

      normalized.forEach(({ id, productGroupId }) => {
        if (!productGroupId) {
          cardKeys.add(`product::${id}`);
          return;
        }

        const groupBuckets = groupTreeCache.get(productGroupId);

        if (!groupBuckets?.size) {
          cardKeys.add(`product::${id}`);
          return;
        }

        let matchedBucket = false;

        for (const [bucketKey, bucket] of groupBuckets) {
          if (!bucket?.productIdSet?.has(id)) {
            continue;
          }

          if (bucket.type === "solids" || bucket.type === "florals") {
            cardKeys.add(bucketKey);
          } else {
            // Home Try On and any other product type
            // remains an individual product card.
            cardKeys.add(`product::${id}`);
          }

          matchedBucket = true;
          break;
        }

        if (!matchedBucket) {
          cardKeys.add(`product::${id}`);
        }
      });

      return cardKeys.size;
    },

    get count() {
      return cardKeys.size;
    },
  };
}
